import {useAppSelector} from "../store";
import {selectCodebooks} from "../store/selectors";
import {JsonPoplistItem} from "../generated-api";
import * as React from "react";
import {CSSProperties, useMemo} from "react";
import {dateToGuiAs} from "../helpers/date";
import {Tooltip} from "@mui/material";

type EventDaysValueProps = {
    eventDays?: string | number[],
    siwiEventDays?: string,
    realEventDays?: string,
    eventId?: number
}

const EventDaysValue = (props: EventDaysValueProps) => {
    const {eventDays, siwiEventDays, realEventDays, eventId} = props;

    const codebooks = useAppSelector(selectCodebooks);
    const eventDayCodebook = codebooks?.['eventDay'];

    return useMemo(() => {
        let humanDays = eventDays;
        let humanTooltip: string[] = [];

        const eventDayList = eventDayCodebook?.filter((item: JsonPoplistItem) => item.value?.indexOf(eventId + ':') === 0);
        const dayTitles = eventDayList?.reduce((map, item) => {
            if (item.label) {
                map[parseInt(item.label)] = dateToGuiAs(item.params?.dayDate as any as string, "d");
            }
            return map;
        }, {} as { [key in number]: string }); // 1: 27, 2: 28, 3: 1, ...

        if (eventId && eventDayList) {
            let isAll = true;
            const list = eventDayList
                .map((item, i) => {
                    const a = (typeof eventDays === 'string'
                        ? eventDays[i] === '1'
                        : eventDays && eventDays.indexOf(i + 1) >= 0);
                    if (a) {
                        humanTooltip.push(dateToGuiAs(item.params?.dayDate as any as string, "eeeee d.M.")
                            + ' (#' + (i + 1) + ')'
                            + (realEventDays?.[i] === '1' ? ' - načteno' : ''));
                        return dayTitles[i + 1];
                    }
                    isAll = false;
                    return ' ' + (dayTitles[i + 1]?.length > 1 ? ' ' : '');
                });
            if (isAll) {
                humanDays = '(všechny)';
            } else if (!list.length) {
                humanDays = '(žádné)';
            } else {
                humanDays = list.join(' ').trimEnd();
            }
        }
        if (!humanTooltip.length && eventDays) {
            humanTooltip = typeof eventDays === 'string' ? [eventDays] : eventDays.map((ed) => '' + ed);
        }
        let siwiDays = undefined;
        if (siwiEventDays && eventDayList) {
            let isAll = true;
            const list = eventDayList
                .map((item, i) => {
                    const a = siwiEventDays[i] === '1';
                    if (a) {
                        return dayTitles[i + 1];
                    }
                    isAll = false;
                    return ' ' + (dayTitles[i + 1]?.length > 1 ? ' ' : '');
                });
            if (isAll) {
                siwiDays = '(všechny)';
            } else if (!list.length) {
                siwiDays = '(žádné)';
            } else {
                siwiDays = list.join(' ').trimEnd();
            }
            if (siwiDays && siwiDays !== humanDays) {
                humanTooltip.push("Siwi: " + siwiDays);
            }
        }

        const emsDayNos = (typeof eventDays === 'string'
            ? eventDays.split('').map((v, i) => v === '1' ? (i + 1) : 0).filter(v => !!v)
            : eventDays) || [];
        const siwiDayNos = siwiEventDays?.split('').map((v, i) => v === '1' ? (i + 1) : 0).filter(v => !!v) || [];

        const merged: JSX.Element[] = [...emsDayNos, ...siwiDayNos]
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort((a, b) => a > b ? 1 : -1)
            .map((dayNo) => {
                const style: CSSProperties = {};
                if (realEventDays?.[dayNo - 1] === '1') {
                    style['color'] = 'var(--green)';
                }
                if (emsDayNos.indexOf(dayNo) >= 0) {
                    if (siwiDayNos.indexOf(dayNo) >= 0 || siwiEventDays === undefined) {
                        // ok
                    } else {
                        // missing in siwi
                        style['color'] = 'var(--color-info)';
                    }
                } else {
                    // missing in EMS
                    style['color'] = 'var(--color-warning)';
                }
                return <span key={dayNo} style={style}>{dayTitles?.[dayNo] || dayNo} </span>;
            });
        let title;
        if (humanDays === siwiDays || siwiEventDays === undefined) {
            title = humanTooltip?.map(t => <div>{t}</div>);
        } else {
            title = <div>
                <pre>{'EMS:  ' + (humanDays ? humanDays : '-')}</pre>
                <br/>
                <pre>{'Siwi: ' + (siwiDays ? siwiDays : '-')}</pre>
            </div>;
        }
        return <Tooltip title={title}>
            <pre className={'event-days'}>{humanDays === '(všechny)' ? humanDays : merged}</pre>
        </Tooltip>;

    }, [eventDays, realEventDays, siwiEventDays, eventDayCodebook, eventId]);
}

export default EventDaysValue;
